"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Plane,
  Clock,
  Shield,
  Truck,
  Building,
  Users,
  MapPin,
  Anchor,
  Snowflake,
  Check,
  ChevronRight,
  Home,
  UserPlus,
} from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import FormaParteModal from "./components/FormaParteModal";
import CargaKPIs from "./components/CargaKPIs";

const CAROUSEL_IMAGES = ["/carga/1.jpg", "/carga/14.jpg", "/carga/5.jpg"];

const REGIONES = ["norte", "centro", "caribe", "sur", "europa"] as const;

export default function CargaPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const t = useTranslations("carga");
  const tCrumb = useTranslations("breadcrumb");
  const locale = useLocale();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const beneficios = [
    { icon: MapPin, key: "conexion", desc: "conexionDesc" },
    { icon: Truck, key: "integracion", desc: "integracionDesc" },
    { icon: Clock, key: "procesos", desc: "procesosDesc" },
    { icon: Shield, key: "infraestructura", desc: "infraestructuraDesc" },
    { icon: Users, key: "ecosistema", desc: "ecosistemaDesc" },
  ] as const;

  const capacidades = [
    { icon: Building, key: "moderna" },
    { icon: Snowflake, key: "cadena" },
    { icon: Anchor, key: "conectividad" },
  ] as const;

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative flex h-[70vh] min-h-[520px] items-end overflow-hidden">
        {CAROUSEL_IMAGES.map((image, index) => (
          <div
            key={image}
            aria-hidden={index !== currentImageIndex}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out motion-reduce:transition-none ${
              index === currentImageIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <Image
              src={image}
              alt=""
              fill
              priority={index === 0}
              sizes="100vw"
              className="object-cover"
            />
          </div>
        ))}

        {/* Doble velo. El plano de color unifica las tres fotos del carrusel —en
            vertical el encuadre se come el cielo plano de dos de ellas y sin
            tinte se leía como una zona vacía— y el degradado asienta el texto. */}
        <div className="absolute inset-0 bg-[#102542]/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/15" />

        <div className="relative z-10 mx-auto w-[90%] max-w-6xl pb-14 md:pb-20">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/80">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-white"
                >
                  <Home className="h-3.5 w-3.5" />
                  <span>{tCrumb("home")}</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3.5 w-3.5 text-white/40" />
              </li>
              <li aria-current="page" className="truncate font-medium text-white">
                {t("hero.breadcrumb")}
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white">
              <span className="h-px w-8 bg-[#ffb800]" />
              {t("hero.eyebrow")}
            </span>
            <h1 className="text-balance text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-6xl">
              {t("hero.title")}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80 md:text-lg">
              {t("hero.subtitle")}
            </p>

            <button
              onClick={() => setIsModalOpen(true)}
              className="mt-8 inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#ffb800] px-6 py-3.5 text-sm font-semibold text-[#102542] transition-colors hover:bg-[#e6a700] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/50"
            >
              <UserPlus className="h-4 w-4" />
              {t("button")}
            </button>
          </div>
        </div>
      </section>

      {/* Cifras de la operación de carga */}
      <CargaKPIs />

      {/* Ventajas */}
      <section className="bg-white">
        <div className="mx-auto w-[90%] max-w-6xl py-16 md:py-24">
          <div className="mb-10 max-w-3xl md:mb-12">
            <span className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#102542]">
              <span className="h-px w-8 bg-[#ffb800]" />
              {t("ventajas.eyebrow")}
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-[#102542] md:text-4xl">
              {t("ventajas.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              {t("ventajas.desc")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200 sm:grid-cols-2 lg:grid-cols-3">
            {beneficios.map(({ icon: Icon, key, desc }) => (
              <article
                key={key}
                className="flex h-full flex-col bg-white p-6 transition-colors hover:bg-gray-50 md:p-8"
              >
                <Icon className="mb-6 h-6 w-6 text-[#102542]" />
                <h3 className="text-lg font-bold tracking-tight text-[#102542]">
                  {t(`ventajas.${key}`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                  {t(`ventajas.${desc}`)}
                </p>
              </article>
            ))}

            {/* Sexta celda: cierra la retícula y remata el argumento */}
            <article className="flex h-full flex-col justify-between bg-[#102542] p-6 md:p-8">
              <Plane className="mb-6 h-6 w-6 text-[#ffb800]" />
              <div>
                <h3 className="text-lg font-bold tracking-tight text-white">
                  {t("ventajas.destaque.title")}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  {t("ventajas.destaque.desc")}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Conectividad */}
      <section className="bg-[#102542]">
        <div className="mx-auto w-[90%] max-w-6xl py-16 md:py-24">
          <div className="mb-10 max-w-3xl md:mb-12">
            <span className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
              <span className="h-px w-8 bg-[#ffb800]" />
              {t("conectividad.eyebrow")}
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
              {t("conectividad.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/70">
              {t("conectividad.desc")}
            </p>
          </div>

          {/* Hub */}
          <div className="flex items-center gap-3 rounded-2xl border border-[#ffb800]/30 bg-[#ffb800]/10 px-6 py-5">
            <Plane className="h-6 w-6 shrink-0 text-[#ffb800]" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#ffb800]">
                {t("conectividad.hub")}
              </p>
              <p className="text-xl font-bold tracking-tight text-white">
                {t("conectividad.panama")}
              </p>
            </div>
          </div>

          {/* Regiones que salen del hub */}
          <div className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
            {REGIONES.map((region) => (
              <div
                key={region}
                className="flex items-center gap-3 bg-[#102542] p-6 transition-colors hover:bg-[#16304f]"
              >
                <MapPin className="h-5 w-5 shrink-0 text-[#ffb800]" />
                <span className="text-sm font-semibold text-white">
                  {t(`conectividad.${region}`)}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-6 text-sm text-white/60">
            {t("conectividad.destinos")}
          </p>
        </div>
      </section>

      {/* Infraestructura y servicios */}
      <section className="bg-editorial">
        <div className="mx-auto w-[90%] max-w-6xl py-16 md:py-24">
          <div className="mb-10 max-w-3xl md:mb-12">
            <span className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#102542]">
              <span className="h-px w-8 bg-[#ffb800]" />
              {t("infra.eyebrow")}
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-[#102542] md:text-4xl">
              {t("infra.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              {t("infra.desc")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200 md:grid-cols-3">
            {capacidades.map(({ icon: Icon, key }) => (
              <article key={key} className="bg-white p-6 md:p-8">
                <Icon className="mb-6 h-6 w-6 text-[#102542]" />
                <h3 className="text-lg font-bold tracking-tight text-[#102542]">
                  {t(`infra.${key}.title`)}
                </h3>
                <ul className="mt-4 space-y-3">
                  {["1", "2", "3"].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-[#ffb800]"
                        aria-hidden
                      />
                      <span className="text-sm leading-relaxed text-gray-600">
                        {t(`infra.${key}.${item}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Cierre */}
      <section className="border-t border-gray-200 bg-white">
        <div className="mx-auto w-[90%] max-w-6xl py-16 md:py-24">
          <div className="flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="mb-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#102542]">
                <span className="h-px w-8 bg-[#ffb800]" />
                {t("cta.eyebrow")}
              </span>
              <h2 className="text-balance text-3xl font-bold tracking-tight text-[#102542] md:text-4xl">
                {t("cta.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-gray-600">
                {t("cta.desc")}
              </p>
            </div>

            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-[#102542] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#16304f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb800] focus-visible:ring-offset-2"
            >
              <UserPlus className="h-4 w-4" />
              {t("cta.button")}
            </button>
          </div>
        </div>
      </section>

      <FormaParteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </main>
  );
}
